import { CmsGrid, ICmsGridColumn, ICmsGridConfig, ICmsGridLabels, ICmsGridPaginationConfig } from "../../common/cms-grid.js";
import { dateToDayString } from "../../utils/date.js";
import { DocumentHistoryVersionsDialog, IDocumentHistoryVersionsDialogConfig } from "./document-history-versions-dialog.js"
import { appendUrlParams } from "../../../components-shared/utils/url-utils.js";
import { IStandardApiError } from "../../../components-shared/api/base/i-standard-api-error.js";

export class DocumentHistoryGrid extends HTMLElement {
    private labels: IDocumentHistoryGridLabels

    constructor(private config: IDocumentHistoryGridConfig, private labelsSource: any) {
        super();

        this.configureFetchInterceptor();
        this.labels = labelsSource as IDocumentHistoryGridLabels;
    }

    /* #region Temporary hack fetch interceptor to work around multiple request bug: https://github.com/grid-js/gridjs/issues/1317 */
    private previousResponseUri: RequestInfo | URL;
    private previousResponsePromise: Promise<Response>;

    private configureFetchInterceptor() {
        const { fetch: originalFetch } = window;
        const pathToIntercept = 'api/document/documents-history';

        window.fetch = async (...args) => {
            let [resource, config ] = args;

            const shouldIntercept = resource.toString().includes(pathToIntercept);

            if (shouldIntercept === true && resource.toString() === this.previousResponseUri) {
                console.info('WORKAROUND: Doc_hist: Duplicate request detected. Returning previous response.');

                const resp = (await this.previousResponsePromise).clone();

                return resp;
            }

            const responsePromise = originalFetch(resource, config);

            if (shouldIntercept === true) {
                this.previousResponseUri = resource.toString();
                this.previousResponsePromise = responsePromise;
            }

            const response = await responsePromise;

            return response.clone();
        };
    }
    /* #endregion */

    /* #region Public */
    public setConfig(config: IDocumentHistoryGridConfig) {
        this.config = config;
        this.grid.setConfig(this.createGridConfig());
    }

    /* #endregion */

    /* #region Private */
    private isInitialized: boolean = false;
    private grid: CmsGrid;
    private historyVersionsDialog: DocumentHistoryVersionsDialog;

    private initialize() {
        
        this.historyVersionsDialog = new DocumentHistoryVersionsDialog(this.config as IDocumentHistoryVersionsDialogConfig, this.labelsSource);
        this.appendChild(this.historyVersionsDialog);

        this.grid = new CmsGrid(this.createGridConfig());
        const versionCommentCellIndex = 6;
        
        this.grid.onCellClicked = (cell: any, column: any, row: any) => { 
            const cellIndex = row.cells.indexOf(cell);

            if (cellIndex === versionCommentCellIndex) {
                const documentId = row.cells[1].data;
                const documentName = row.cells[2].data;
                
                this.historyVersionsDialog.initialize({documentId: documentId, documentName: documentName});
                this.historyVersionsDialog.showModal();
            }
            else {
                const url = row.cells[0].data;

                if (url) {
                    document.location = url;
                }
            }
        };

        this.appendChild(this.grid);
    }

    private createGridConfig() {
        const columns = this.createGridConfigColumns();

        return {
            apiBaseUrl: this.createApiBaseUrl(),
            apiDataMapper: (data: any) => data.pageDocs.map((doc: any) => columns.map(c => doc[c.dataKey])),
            apiDataTotalMapper: (data: any) => data.totalCount,
            columns: columns,
            labels: this.labels as ICmsGridLabels,
            sortable: true,
            pagination: {
                itemsPerPage: this.config.itemsPerPage,
            } as ICmsGridPaginationConfig
        } as ICmsGridConfig;
    }

    private createApiBaseUrl() {
        let url = `/api/document/documents-history?rootFolderId=${this.config.rootFolderId}&tagCategoriesRootId=${this.config.tagCategoriesRootId}`;

        if (this.config.fromDate) {
            url = appendUrlParams(url, { fromDate: dateToDayString(this.config.fromDate) });
        }

        if (this.config.searchTerm) {
            url = appendUrlParams(url, { term: this.config.searchTerm });
        }

        if (this.config.filterGroups && this.config.filterGroups.length) {
            url = appendUrlParams(url, { filterGroups: this.config.filterGroups });
        }

        if (this.config.filterTags && this.config.filterTags.length) {
            url = appendUrlParams(url, { filterTags: this.config.filterTags });
        }

        return url;
    }

    private createGridConfigColumns() {
        return [
            { dataKey: "url", hidden: true },
            { dataKey: "id", label: "ID", sortable: true },
            { dataKey: "name", label: this.labels.articleName, sortable: true },
            { 
                dataKey: "groups", 
                label: this.labels.articleGroup, 
                sortable: false, 
                html: true, 
                formatter: (cell: any, _: any) => cell && cell.length ? `<ul>${cell.map(c => `<li>${c}</li>`).join('')}</ul>` : ""
            },
            /*{ dataKey: "updated", label: this.labels.updatedDate, sortable: true },*/
            { dataKey: "created", label: this.labels.createdDate, sortable: true },
            { dataKey: "versionDate", label: this.labels.versionDate, sortable: true },
            { 
                dataKey: "comment", 
                label: this.labels.versionComment, 
                sortable: false,
                html: true,
                formatter: (cell: any, _: any) => { const cellText = cell || this.labels.noVersionComment; return `<div class="versionComment">${cellText}</div><i class="fa fa-search" aria-hidden="true"></i>` }
            },
            { 
                dataKey: "tags", 
                label: this.labels.tags, 
                sortable: false,
                formatter: (cell: any, _: any) => cell && cell.length ? cell.join(', ') : ""
            }
        ] as ICmsGridColumn[];
    }

    /* #endregion */

    connectedCallback() {
        if (!this.isInitialized) {
            this.isInitialized = true;
            this.initialize();
        }
    }
}

export interface IDocumentHistoryGridConfig extends IDocumentHistoryVersionsDialogConfig {
    itemsPerPage: number;
    searchTerm: string;
    fromDate: Date;
    rootFolderId: number;
    tagCategoriesRootId: number;
    filterGroups: string[];
    filterTags: string[];
    screenshotsRelationsIDs: number[];
    screenshotImageWidth: number;
    screenshotImageModification: string;
    renderPageId: number;
    errorReporter: (error: IStandardApiError) => void;
}

export interface IDocumentHistoryGridLabels extends ICmsGridLabels {
    articleName: string;
    articleGroup: string;
    createdDate: string;
    updatedDate: string
    versionDate: string;
    versionComment: string;
    tags: string;
    noVersionComment: string;
}

customElements.define("document-history-grid", DocumentHistoryGrid);